import React, { useState, useEffect, useRef } from 'react';
import downloadDetailIcon from '../images/download-detail.png';
import informationIcon from '../images/information.svg';
import fallbackLogo from '../images/recorb-app.svg';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { Modal } from 'antd';

// Types copied from RecorbPage
type FormState = {
  downloadVersion: string;
  agentName: string;
  agentPersonInCharge: string;
  agencyEmail: string;
  endUserName: string;
  endUserContactName: string;
  endUserEmail: string;
  downloadInfoEmail: string;
}

type ValidationErrors = {
  [key in keyof FormState]?: string;
}

// Session utils copied from RecorbPage
const getSessionData = () => {
  try {
    const agentPersonInCharge = sessionStorage.getItem('agentPersonInCharge') || '';
    const agentName = sessionStorage.getItem('agentName') || '';
    const agencyEmail = sessionStorage.getItem('agencyEmail') || '';
    return { agentPersonInCharge, agentName, agencyEmail };
  } catch (error) {
    return { agentPersonInCharge: '', agentName: '', agencyEmail: '' };
  }
};

// Public app type
type PublicApp = {
  id: number;
  name: string;
  description: string;
  appInfo?: string | null;
  logoUrl: string;
  latestVersion?: string | null;
};

type PublicVersion = {
  id: number;
  version: string;
  changes: string;
  releaseDate?: string;
};

const AppDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState<PublicApp | null>(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [versions, setVersions] = useState<PublicVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<PublicVersion | null>(null);

  // Fetch public app detail
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/api/app/apps/${id}`);
        if (!mounted) return;
        setApp(res.data);
      } catch (e) {
        // ignore and keep fallback
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Fetch versions for this app
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/api/app/apps/${id}/versions`);
        if (!mounted) return;
        const items = (res.data?.items ?? []) as PublicVersion[];
        setVersions(items);
        if (items.length > 0) {
          setSelectedVersion(items[0]);
        }
      } catch (e) {
        setVersions([]);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Form state
  const [formState, setFormState] = useState<FormState>(() => {
    const sessionData = getSessionData();
    return {
      downloadVersion: '1.0.0',
      agentName: sessionData.agentName,
      agentPersonInCharge: sessionData.agentPersonInCharge,
      agencyEmail: sessionData.agencyEmail,
      endUserName: '',
      endUserContactName: '',
      endUserEmail: '',
      downloadInfoEmail: ''
    };
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Update initial version from app.latestVersion if present
  useEffect(() => {
    if (selectedVersion?.version) {
      setFormState((prev) => ({ ...prev, downloadVersion: selectedVersion.version }));
    } else if (app?.latestVersion) {
      setFormState((prev) => ({ ...prev, downloadVersion: app.latestVersion! }));
    }
  }, [selectedVersion?.version, app?.latestVersion]);

  // helpers copied from RecorbPage
  const getEmailOptions = () => {
    const options = [] as { value: string; label: string }[];
    if (formState.endUserEmail) options.push({ value: formState.endUserEmail, label: formState.endUserEmail });
    if (formState.agencyEmail) options.push({ value: formState.agencyEmail, label: formState.agencyEmail });
    return options;
  };

  useEffect(() => {
    const sessionData = getSessionData();
    if (sessionData.agencyEmail && formState.agencyEmail !== sessionData.agencyEmail) {
      setFormState(prev => ({ ...prev, agencyEmail: sessionData.agencyEmail }));
    }
    if (sessionData.agentName && formState.agentName !== sessionData.agentName) {
      setFormState(prev => ({ ...prev, agentName: sessionData.agentName }));
    }
    if (sessionData.agentPersonInCharge && formState.agentPersonInCharge !== sessionData.agentPersonInCharge) {
      setFormState(prev => ({ ...prev, agentPersonInCharge: sessionData.agentPersonInCharge }));
    }
    const emailOptions = getEmailOptions();
    if (emailOptions.length > 0 && !formState.downloadInfoEmail) {
      setFormState(prev => ({ ...prev, downloadInfoEmail: emailOptions[0].value }));
    }
  }, [formState.endUserEmail, formState.agencyEmail]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};
    if (!formState.downloadVersion) newErrors.downloadVersion = 'Download version is required';
    if (!formState.agentName) newErrors.agentName = 'Agent name is required';
    if (!formState.agentPersonInCharge) newErrors.agentPersonInCharge = 'Person in charge is required';
    if (!formState.agencyEmail) newErrors.agencyEmail = 'Agency email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.agencyEmail)) newErrors.agencyEmail = 'Please enter a valid email address';
    if (!formState.endUserName) newErrors.endUserName = 'End user name is required';
    if (!formState.downloadInfoEmail) newErrors.downloadInfoEmail = 'Download info email is required';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      const configData = {
        SerialNumber: '',
        AgentName: formState.agentName,
        AgencyContactPerson: formState.agentPersonInCharge,
        AgencyEmail: formState.agencyEmail,
        ClinicName: formState.endUserName,
        UserContactName: formState.endUserContactName,
        UserContactEmail: formState.endUserEmail,
        NotificationEmail: formState.downloadInfoEmail
      };
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      // include app id and selected version for server-side packaging
      const response = await axios.post(`${backendUrl}/api/app/download`, {
        appId: Number(id),
        version: formState.downloadVersion,
        config: configData,
      });
      if (response.status === 200) {
        setShowSuccessPopup(true);
      } else {
        setSubmitError('Failed to process your request. Please try again later.');
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const featureItems = selectedVersion ? [selectedVersion.changes] : [app?.description || ''];

  return (
    <div className="recorb-page px-6 py-8 max-w-4xl mx-auto">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        {!logoLoaded && <div className="h-[53px] w-[190px] bg-gray-200 animate-pulse rounded-md"></div>}
        <img
          src={app?.logoUrl || fallbackLogo}
          alt={app?.name || 'App Logo'}
          className={`h-[53px] w-[190px] object-contain ${!logoLoaded ? 'invisible absolute' : ''}`}
          onLoad={() => setLogoLoaded(true)}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = fallbackLogo as unknown as string;
          }}
        />
      </div>

      {/* Application Information */}
      <div>
        <div className="py-2 px-4">
          <h3 className="font-semibold inline-block bg-[#0077C8] text-white py-1 px-3">Application Information</h3>
        </div>
        <div className="py-4 px-6">
          <p className="text-sm leading-relaxed">
            {app?.appInfo || app?.description || 'Application information will be shown here.'}
          </p>
        </div>
      </div>
      <hr className="my-6" />

      {/* Version */}
      <div className="py-2 px-4 mb-6">
        <p className="font-semibold inline-block bg-[#0077C8] text-white py-1 px-3 border border-[#DADEE3]">
          Version {formState.downloadVersion}
        </p>
      </div>

      {/* Feature List (uses selected version changes) */}
      <div className="mb-6">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 list-disc pl-8">
          {featureItems.map((item, index) => (
            <li key={index} className="text-sm">{item}</li>
          ))}
        </ul>
      </div>

      {/* Form Heading */}
      <div className="mb-4 flex items-center">
        <img src={informationIcon} alt="Information" className="h-5 w-5 mr-2" />
        <h4 className="font-bold text-lg">Please enter the following information to download the app.</h4>
      </div>

      {/* Error */}
      {submitError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {submitError}
        </div>
      )}

      {/* Form (copied from RecorbPage) */}
      <form ref={formRef} onSubmit={handleSubmit} className="bg-[#F4F6F9] border border-[#BCC2FF] rounded-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Download Version */}
          <div className="form-group">
            <label htmlFor="downloadVersion" className="block text-sm font-medium mb-1">Download Version <span className="text-red-600 align-middle leading-none">※</span></label>
            <select
              id="downloadVersion"
              name="downloadVersion"
              value={formState.downloadVersion}
              onChange={(e) => {
                const ver = e.target.value;
                const found = versions.find(v => v.version === ver) || null;
                setSelectedVersion(found);
                setFormState(prev => ({ ...prev, downloadVersion: ver }));
              }}
              required
              className={`w-full border ${errors.downloadVersion ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {versions.length > 0 ? (
                versions.map((v) => (
                  <option key={v.id} value={v.version}>Version {v.version}</option>
                ))
              ) : (
                <option value={formState.downloadVersion}>Version {formState.downloadVersion}</option>
              )}
            </select>
            {errors.downloadVersion && (
              <p className="text-red-500 text-xs mt-1">{errors.downloadVersion}</p>
            )}
          </div>

          {/* End User (Clinic) name */}
          <div className="form-group">
            <label htmlFor="endUserName" className="block text-sm font-medium mb-1">End User (Clinic) name <span className="text-red-600 align-middle leading-none">※</span></label>
            <input
              type="text"
              id="endUserName"
              name="endUserName"
              value={formState.endUserName}
              onChange={handleChange}
              required
              className={`w-full border ${errors.endUserName ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2`}
            />
            {errors.endUserName && (
              <p className="text-red-500 text-xs mt-1">{errors.endUserName}</p>
            )}
          </div>

          {/* Agent Name */}
          <div className="form-group">
            <label htmlFor="agentName" className="block text-sm font-medium mb-1">Agent Name <span className="text-red-600 align-middle leading-none">※</span></label>
            <input
              type="text"
              id="agentName"
              name="agentName"
              value={formState.agentName}
              onChange={handleChange}
              required
              disabled
              className={`w-full border ${errors.agentName ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.agentName && (
              <p className="text-red-500 text-xs mt-1">{errors.agentName}</p>
            )}
          </div>

          {/* End user contact name */}
          <div className="form-group">
            <label htmlFor="endUserContactName" className="block text-sm font-medium mb-1">End user contact name</label>
            <input
              type="text"
              id="endUserContactName"
              name="endUserContactName"
              value={formState.endUserContactName}
              onChange={handleChange}
              className={`w-full border ${errors.endUserContactName ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.endUserContactName && (
              <p className="text-red-500 text-xs mt-1">{errors.endUserContactName}</p>
            )}
          </div>

          {/* Name of person in charge of the agency */}
          <div className="form-group">
            <label htmlFor="agentPersonInCharge" className="block text-sm font-medium mb-1">Name of person in charge of the agency <span className="text-red-600 align-middle leading-none">※</span></label>
            <input
              type="text"
              id="agentPersonInCharge"
              name="agentPersonInCharge"
              value={formState.agentPersonInCharge}
              onChange={handleChange}
              required
              disabled
              className={`w-full border ${errors.agentPersonInCharge ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 bg-gray-100 text-gray-700`}
            />
            {errors.agentPersonInCharge && (
              <p className="text-red-500 text-xs mt-1">{errors.agentPersonInCharge}</p>
            )}
          </div>

          {/* End user contact email address */}
          <div className="form-group">
            <label htmlFor="endUserEmail" className="block text-sm font-medium mb-1">End user contact email address</label>
            <input
              type="email"
              id="endUserEmail"
              name="endUserEmail"
              value={formState.endUserEmail}
              onChange={handleChange}
              className={`w-full border ${errors.endUserEmail ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2`}
            />
            {errors.endUserEmail && (
              <p className="text-red-500 text-xs mt-1">{errors.endUserEmail}</p>
            )}
          </div>

          {/* Agency contact email address */}
          <div className="form-group">
            <label htmlFor="agencyEmail" className="block text.sm font-medium mb-1">Agency contact email address <span className="text-red-600 align-middle leading-none">※</span></label>
            <input
              type="email"
              id="agencyEmail"
              name="agencyEmail"
              value={formState.agencyEmail}
              onChange={handleChange}
              required
              disabled
              className={`w-full border ${errors.agencyEmail ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.agencyEmail && (
              <p className="text-red-500 text-xs mt-1">{errors.agencyEmail}</p>
            )}
          </div>

          {/* Email address to send download information */}
          <div className="form-group">
            <label htmlFor="downloadInfoEmail" className="block text-sm font-medium mb-1">Email address to send download information <span className="text-red-600 align-middle leading-none">※</span></label>
            <select
              id="downloadInfoEmail"
              name="downloadInfoEmail"
              value={formState.downloadInfoEmail}
              onChange={handleChange}
              required
              className={`w-full border ${errors.downloadInfoEmail ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {getEmailOptions().length === 0 ? (
                <option value="">No email available</option>
              ) : (
                getEmailOptions().map((option, index) => (
                  <option key={index} value={option.value}>{option.value}</option>
                ))
              )}
            </select>
            {errors.downloadInfoEmail && (
              <p className="text-red-500 text-xs mt-1">{errors.downloadInfoEmail}</p>
            )}
          </div>
        </div>
      </form>

      {/* Download Button */}
      <div className="flex justify-center col-span-1 md:col-span-2 mt-4">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="border-0 bg-transparent p-0 relative"
        >
          {isSubmitting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={downloadDetailIcon}
            alt={`Download ${app?.name || 'App'}`}
            className={`h-auto max-h-[60px] hover:opacity-80 transition-opacity ${isSubmitting ? 'opacity-70' : ''}`}
          />
        </button>
      </div>

      {/* Success Popup */}
      <Modal
        title="Success"
        open={showSuccessPopup}
        onOk={() => navigate('/')}
        onCancel={() => navigate('/')}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p className="text-gray-700">
          Your request has been received. The app configuration will be sent to {formState.downloadInfoEmail} shortly. Please allow a few minutes before checking your inbox.
        </p>
      </Modal>
    </div>
  );
};

export default AppDetailPage;
